import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { 
  Paper,
  TextField,
  IconButton,
  CircularProgress,
  Box,
  Typography,
  Avatar,
} from '@material-ui/core';
import { 
  AttachFile as AttachFileIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
} from '@material-ui/icons';
import useStyles from './style';
import { getSignedUrl, upload } from 'utils/aws/s3';
import { getFileType } from 'utils/files';
import { FileViewer } from 'components';

const MessageAdd = (props) => {
  const {
    onSend,
    permissions,
    setMessages,
    setShouldScrollToBottom,
  } = props;

  const classes = useStyles();
  const [newMessage, setNewMessage] = useState('');
  const [isSubmittingAdd, setIsSubmittingAdd] = useState(false);
  const [isSubmittingAddFile, setIsSubmittingAddFile] = useState(false);
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [modal, setModal] = useState(null);

  const handleSend = async () => {
    if (newMessage.trim() === '' || isSubmittingAdd) return;

    setIsSubmittingAdd(true);
    
    try {
      if (onSend) {
        await onSend(newMessage).then((newSubmitMessage) => {
          setShouldScrollToBottom(true);
          setMessages((prevMessage) => [...prevMessage, newSubmitMessage]);
        });
      }
      setNewMessage('');
      setFiles([]);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSubmittingAdd(false);
    }
  };

  const handleFileSelect = async (event) => {
    const selectedFiles = Array.from(event.target.files);
    if (selectedFiles.length === 0) return;

    setIsSubmittingAddFile(true);
    const newFiles = [];
    
    for await (const [index, selectedFile] of selectedFiles.entries()) {
      const ext = ((selectedFile?.name ?? '').split('.').pop() || '').toLowerCase();
      const s3File = await upload(
        `${'tmp/'}${new Date().getTime()}.${String(Math.random()).substring(
          2,
          7,
        )}${ext ? `.${ext.toLowerCase()}` : ''}`,
        selectedFile,
        {},
      );

      const signedUrl = await getSignedUrl(s3File.Location, 120);

      const { type } = getFileType(ext);

      newFiles.push({
        id: `file-${Date.now()}-${index}`,
        name: `${selectedFile?.name || ''}${selectedFile.length > 1 ? (` ${newFiles.length + 1}`) : ''}`,
        signed: signedUrl,
        type,
        url: s3File.Location,
      });
    };

    setFiles((prev) => [...prev, ...newFiles]);
    setIsSubmittingAddFile(false);
    
    event.target.value = null;
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((file) => file.id !== id));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isSubmittingAdd) {
      e.preventDefault();
      handleSend();
    }
  };

  const showModal = (data) => {
    setModal({
      open: true,
      data,
    });
  };
  
  const closeModal = () => {
    setModal(null);
  };

  if (!permissions.add) return null;

  return (
    <Box className={classes.inputContainer}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        style={{ 
          display: 'none',
        }}
        multiple
      />

      {modal?.data && (
        <FileViewer
          data={modal.data}
          onClose={() => closeModal()}
          modal={modal?.open || false}
          actions={true}
          thumbs={true}
        />
      )}
      
      {files.length > 0 && (
        <Box className={classes.filesPreviewContainer}>
          {files.map((file) => (
            <Box 
              key={file.id} 
              className={classes.fileThumbnail}
              sx={{ 
                position: 'relative',
                cursor: 'pointer',
              }}
              onClick={() => showModal(file.url)}
            >
              {file.type === 'image' ? (
                <img 
                  src={file.signed} 
                  alt={file.name} 
                  className={classes.fileImage}
                />
              ) : (
                <Avatar className={classes.fileDocument}>
                  <FileIcon fontSize="large" />
                </Avatar>
              )}
              <Box className={classes.fileInfo}>
                <Typography variant="caption" noWrap>
                  {file.name}
                </Typography>
              </Box>
              <IconButton 
                size="small" 
                className={classes.fileRemoveButton}
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(file.id);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
      
      <Paper elevation={0} className={classes.inputPaper}>
        <TextField
          className={classes.textField}
          placeholder="Escribe un mensaje..."
          multiline
          maxRows={4}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={handleKeyPress}
          variant="outlined"
          InputProps={{
            style: {
              padding: '8px 16px',
            },
          }}
          disabled={isSubmittingAdd}
        />
        {permissions.files && (
          <IconButton 
            className={classes.fileButton} 
            onClick={triggerFileInput}
            disabled={isSubmittingAddFile}
          >
            {isSubmittingAddFile ? (
              <CircularProgress size={24} />
            ) : (
              <AttachFileIcon />
            )}
          </IconButton>
        )}
        <IconButton 
          className={classes.sendButton} 
          onClick={handleSend}
          disabled={(newMessage.trim() === '' && files.length === 0) || isSubmittingAdd}
        >
          {isSubmittingAdd ? (
            <CircularProgress size={24} />
          ) : (
            <SendIcon />
          )}
        </IconButton>
      </Paper>
    </Box>
  );
};

MessageAdd.propTypes = {
  onSend: PropTypes.func.isRequired,
  permissions: PropTypes.shape({
    add: PropTypes.bool,
    files: PropTypes.bool,
  }),
  setMessages: PropTypes.func.isRequired,
  setShouldScrollToBottom: PropTypes.func.isRequired,
};

MessageAdd.defaultProps = {
  permissions: {
    add: false,
    files: false,
  },
};

export default MessageAdd;
